/**
 * Fix 3 (WP-577 review re-review, TEN-2918 follow-up, sibling of the batch-preview
 * `originalItems` fix in `lib/batchCaptureOutput.ts`): the single-capture preview's
 * "Capture for real" next-action used to advertise only
 * `{ collection, name, description }` — dropping `data`, `entryId`, `canonicalKey`, `links`,
 * `sourceRef`, `sourceExcerpt`, `autoCommit`, `ownerTeamEntryId`, `ownerRoleEntryId`. An agent
 * that followed the hint literally created a MATERIALLY DIFFERENT entry than the one the
 * preview said would succeed — e.g. a required WHY supplied via `data` disappeared and the
 * retry hit a governance block the preview never predicted.
 *
 * Extracted (rather than inlined at both call sites) so `smart-capture.ts` — grandfathered
 * shrink-only under STD-2 / DEC-1504 — pays for the extra fields here instead of widening its
 * own baseline, and so the single-capture preview branch and the `suggestOnly` grounding
 * preview (same underlying defect) share one source of truth instead of drifting.
 */
import type { NextAction } from "../envelope.js";

export type CaptureForRealPayload = {
  collection: string;
  name: string;
  description: string;
  context?: string;
  entryId?: string;
  canonicalKey?: string;
  data?: Record<string, unknown>;
  links?: Array<{ to: string; type: string }>;
  autoCommit?: boolean;
  sourceRef?: string;
  sourceExcerpt?: string;
  ownerTeamEntryId?: string;
  ownerRoleEntryId?: string;
};

/**
 * Builds the single "Capture for real" next-action, including every field the caller actually
 * supplied (omitting undefined/empty ones so the suggestion stays clean and additive-safe).
 */
export function buildCaptureForRealNextAction(payload: CaptureForRealPayload): NextAction[] {
  const {
    collection, name, description, context, entryId, canonicalKey, data, links,
    autoCommit, sourceRef, sourceExcerpt, ownerTeamEntryId, ownerRoleEntryId,
  } = payload;
  return [{
    tool: "capture",
    description: "Capture for real",
    parameters: {
      collection,
      name,
      description,
      ...(context ? { context } : {}),
      ...(entryId ? { entryId } : {}),
      ...(canonicalKey ? { canonicalKey } : {}),
      ...(data && Object.keys(data).length > 0 ? { data } : {}),
      ...(links && links.length > 0 ? { links } : {}),
      ...(autoCommit !== undefined ? { autoCommit } : {}),
      ...(sourceRef ? { sourceRef } : {}),
      ...(sourceExcerpt ? { sourceExcerpt } : {}),
      ...(ownerTeamEntryId ? { ownerTeamEntryId } : {}),
      ...(ownerRoleEntryId ? { ownerRoleEntryId } : {}),
    },
  }];
}
